import { PayloadAction } from '@reduxjs/toolkit';

import {
  signInBL,
  signOutBL,
  signUpBL,
  isFrictionlessLoginEnabledBL,
  refreshAuthBL,
} from 'businessLogic/Auth/Auth';
import { makeDebounce } from 'components/Shared/FrictionlessLogin/debounce';
import segmentIO from 'reporting/SegmentIO';
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import { emailRegex } from 'shared/freeTextModalValidators';
import { insightSelectors } from 'store/insight/selectors';
import { isFrictionlessPhase2SubscriptionsEnabled } from 'store/ixp/selector';
import { setIsSavePaymentMethodChecked } from 'store/payment/slice';
import { isForcedAutopayRecurringSelector } from 'store/sale/selectors';
import { sliceFactory, thunkActionFactory } from 'store/utils';
import { Auth, AuthSignedIn } from 'types/Auth';

const splunkReporter = SplunkReporter.getInstance();

const initialState: Auth = {
  isFrictionlessLoginEnabled: false,
  frictionlessLoginPhase3: {
    shouldShowIdentitySignInWidget: false,
    shouldShowSignInCTA: false,
    isIdentitySignInWidgetError: false,
    isIdentitySignInWidgetCompleted: false,
    isSignInCTADecisionPending: true,
  },
  frictionlessCheckout: {
    isSelected: false,
    isDetailsRequired: false,
    isDetailsModalClosed: undefined,
  },
  isGenerateFactorLoading: false,
} as Auth;

const { actions, reducer } = sliceFactory({
  name: 'auth',
  initialState,
  reducers: {
    signOutFailed(state, action: PayloadAction<{ error?: Auth['authError'] }>) {
      const { error } = action.payload || {};
      if (error) {
        state.authError = error;
      }
    },
    setFrictionlessLogin(state, action: PayloadAction<{ isEnabled: boolean }>) {
      const { isEnabled } = action.payload || {};
      state.isFrictionlessLoginEnabled = isEnabled;
    },
    refreshAuthenticationSuccessful(state, action: PayloadAction<{ auth: AuthSignedIn }>) {
      return {
        ...state,
        ...action.payload.auth,
      };
    },
    setFrictionlessCheckoutSelected: (state, action) => {
      if (!state.frictionlessCheckout) {
        state.frictionlessCheckout = { isSelected: false, isDetailsRequired: false };
      }

      const prevState = state.frictionlessCheckout.isSelected;
      const newState = action.payload.frictionlessCheckoutSelected;

      if (prevState !== newState) {
        segmentIO.frictionlessCheckoutToggle(newState ? 'on' : 'off');
      }

      state.frictionlessCheckout.isSelected = newState;
    },
    setFrictionlessCheckoutDetailsRequired(
      state,
      action: PayloadAction<{ isDetailsRequired: boolean }>
    ) {
      if (!state.frictionlessCheckout) {
        state.frictionlessCheckout = { isSelected: false, isDetailsRequired: false };
      }
      state.frictionlessCheckout.isDetailsRequired = action.payload.isDetailsRequired;
    },
    setFrictionlessCheckoutDetailsModalClosed(
      state,
      action: PayloadAction<{ isDetailsModalClosed: boolean }>
    ) {
      state.frictionlessCheckout.isDetailsModalClosed = action.payload.isDetailsModalClosed;
    },
    setFrictionlessCheckoutEmail(state, action: PayloadAction<{ email?: string }>) {
      if (!state.frictionlessCheckout) {
        state.frictionlessCheckout = { isSelected: false, isDetailsRequired: false };
      }
      state.frictionlessCheckout.email = action.payload.email;
    },
    setFrictionlessCheckoutPhone(state, action: PayloadAction<{ phone?: string }>) {
      if (!state.frictionlessCheckout) {
        state.frictionlessCheckout = { isSelected: false, isDetailsRequired: false };
      }
      state.frictionlessCheckout.phone = action.payload.phone;
    },
    updateFrictionlessCheckoutCountry(state, action: PayloadAction<Record<string, any>>) {
      if (!state.frictionlessCheckout) {
        state.frictionlessCheckout = { isSelected: false, isDetailsRequired: false };
      }
      state.frictionlessCheckout.phone = state.frictionlessCheckout.phone?.replace(
        state.frictionlessCheckout?.countryCode?.dial_code || '',
        action.payload.dial_code
      );
      state.frictionlessCheckout.countryCode = action.payload;
    },
    setFrictionlessCheckoutClosedOTP(state, action: PayloadAction<{ isUserClosedOTP: boolean }>) {
      state.frictionlessCheckout.isUserClosedOTP = action.payload.isUserClosedOTP;
    },
    setFrictionlessCheckoutComplete(state, action: PayloadAction<{ isCompleted: boolean }>) {
      state.frictionlessCheckout.isCompleted = action.payload.isCompleted;
    },
    setFrictionlessCheckoutWalletCreated(
      state,
      action: PayloadAction<{ isWalletCreated: boolean }>
    ) {
      state.frictionlessCheckout.isWalletCreated = action.payload.isWalletCreated;
    },
    setUserAlreadySignedUp(state) {
      state.frictionlessCheckout.isUserAlreadySignedUp = true;
    },
    setFrictionlessUserNameAvailable(
      state,
      action: PayloadAction<{ isUserNameAvailable: boolean }>
    ) {
      state.frictionlessCheckout.isUserNameAvailable = action.payload.isUserNameAvailable;
    },
    setFrictionlessRegistrationRequired(
      state,
      action: PayloadAction<{ isRegistrationRequired: boolean }>
    ) {
      state.frictionlessCheckout.isRegistrationRequired = action.payload.isRegistrationRequired;
    },
    setShouldShowIdentitySignInWidget(
      state,
      action: PayloadAction<{
        shouldShowIdentitySignInWidget: boolean;
      }>
    ) {
      if (!state.frictionlessLoginPhase3) {
        state.frictionlessLoginPhase3 = { ...initialState.frictionlessLoginPhase3 };
      }
      state.frictionlessLoginPhase3.shouldShowIdentitySignInWidget =
        action.payload.shouldShowIdentitySignInWidget;
    },
    setShouldShowSignInCTA(state, action: PayloadAction<{ shouldShowSignInCTA: boolean }>) {
      if (!state.frictionlessLoginPhase3) {
        state.frictionlessLoginPhase3 = { ...initialState.frictionlessLoginPhase3 };
      }
      state.frictionlessLoginPhase3.shouldShowSignInCTA = action.payload.shouldShowSignInCTA;
    },
    setIsSignInCTADecisionPending(
      state,
      action: PayloadAction<{ isSignInCTADecisionPending: boolean }>
    ) {
      if (!state.frictionlessLoginPhase3) {
        state.frictionlessLoginPhase3 = { ...initialState.frictionlessLoginPhase3 };
      }
      state.frictionlessLoginPhase3.isSignInCTADecisionPending =
        action.payload.isSignInCTADecisionPending;
    },
    setIsIdentitySignInWidgetError(
      state,
      action: PayloadAction<{ isIdentitySignInWidgetError: boolean }>
    ) {
      if (!state.frictionlessLoginPhase3) {
        state.frictionlessLoginPhase3 = { ...initialState.frictionlessLoginPhase3 };
      }
      state.frictionlessLoginPhase3.isIdentitySignInWidgetError =
        action.payload.isIdentitySignInWidgetError;
    },
    setIsIdentitySignInWidgetCompleted(
      state,
      action: PayloadAction<{ isIdentitySignInWidgetCompleted: boolean }>
    ) {
      if (!state.frictionlessLoginPhase3) {
        state.frictionlessLoginPhase3 = { ...initialState.frictionlessLoginPhase3 };
      }
      state.frictionlessLoginPhase3.isIdentitySignInWidgetCompleted =
        action.payload.isIdentitySignInWidgetCompleted;
    },
    setFrictionlessLoginEmail(state, action: PayloadAction<{ email: string }>) {
      state.frictionlessLoginEmail = action.payload.email;
    },
    setFrictionlessLoginSessionId(state, action: PayloadAction<{ sessionId: string }>) {
      state.frictionlessLoginSessionId = action.payload.sessionId;
    },
    clearFrictionlessLoginState(state) {
      state.frictionlessLoginEmail = undefined;
      state.frictionlessLoginSessionId = undefined;
      // Also clear frictionless checkout email to reset to original state
      if (state.frictionlessCheckout) {
        state.frictionlessCheckout.email = undefined;
      }
    },
    setGenerateFactorLoading: (state, action: PayloadAction<{ isLoading: boolean }>) => {
      state.isGenerateFactorLoading = action.payload.isLoading;
    },
    setFrictionlessIdentityError: (
      state,
      action: PayloadAction<{ showError: boolean; errorMessage?: string }>
    ) => {
      if (!state.frictionlessCheckout) {
        state.frictionlessCheckout = { isSelected: false, isDetailsRequired: false };
      }
      state.frictionlessCheckout.showIdentityError = action.payload.showError;
      state.frictionlessCheckout.identityErrorMessage = action.payload.errorMessage;
    },
  },
});

export const authReducers = reducer;
export const authActions = actions;

export const signIn = thunkActionFactory(({ state }) => {
  const {
    auth: { entityId },
    config: { endpoints },
  } = state;
  state.auth.entityId;
  signInBL({ entityId, endpoints });
});

export const signUp = thunkActionFactory(({ state }) => {
  const {
    auth: { entityId },
    config: { endpoints },
  } = state;
  signUpBL({ entityId, endpoints });
});

export const signOut = thunkActionFactory(async ({ dispatch, state }) => {
  let token;
  const {
    insight,
    config: { ssrtid, portal },
  } = state;

  token = insightSelectors.tokenSelector(insight);

  try {
    await signOutBL({ ssrtid, portal, token });
  } catch (error: any) {
    dispatch(authActions.signOutFailed({ error }));
  }
});

export const checkFrictionlessLogin = thunkActionFactory(async ({ dispatch, state }) => {
  const {
    auth: { isUserSignedIn, frictionlessLoginEmail, frictionlessLoginSessionId },
    featureFlags,
    sale,
    insight,
  } = state;

  const isForcedAutopayRecurring = isForcedAutopayRecurringSelector(state);

  const isEnabled = await isFrictionlessLoginEnabledBL({
    isUserSignedIn,
    frictionlessLoginEmail,
    frictionlessLoginSessionId,
    featureFlags,
    sale,
    insight,
    isForcedAutopayRecurring,
  });
  dispatch(authActions.setFrictionlessLogin({ isEnabled }));
});

/**
 * Generate factor token and set it as frictionless login session ID
 * This action calls the SSR endpoint to get a factor token from SCS API
 */
export const generateFactor = thunkActionFactory(async ({ dispatch, state, businessLogic }) => {
  const {
    auth: { frictionlessLoginEmail, frictionlessCheckout },
    insight,
    featureFlags,
    ixp,
  } = state;
  const token = insightSelectors.tokenSelector(insight);

  if (!frictionlessLoginEmail) {
    throw new Error('Email is required to generate factor');
  }

  // Additional check: verify email exists before calling generateFactor
  // Check if this is subscription or forced autopay flow to determine which field to check
  const isForcedAutopayRecurring = isForcedAutopayRecurringSelector(state);
  const isSubscriptionFLPhase2 = isFrictionlessPhase2SubscriptionsEnabled({ featureFlags, ixp });

  const isEmailExists =
    isSubscriptionFLPhase2 || isForcedAutopayRecurring
      ? !frictionlessCheckout?.isUserNameAvailable
      : false;

  if (!isEmailExists) {
    // Email doesn't exist, don't proceed with generateFactor
    return null;
  }

  // Set loading state to true
  dispatch(authActions.setGenerateFactorLoading({ isLoading: true }));

  try {
    // Call business logic to get factor token from SSR endpoint
    const response = await businessLogic.auth.generateFactor({
      email: frictionlessLoginEmail,
      token,
    });

    // Set the factor token as the session ID for frictionless login
    dispatch(
      authActions.setFrictionlessLoginSessionId({
        sessionId: response.factorToken,
      })
    );

    return response.factorToken;
  } catch (error: any) {
    // Log error but don't throw to avoid breaking the flow
    splunkReporter.contextual({
      event: 'pay',
      action: 'frictionlessLogin',
      logInfo: { logLevel: 'error', logger: 'store/auth/slice.ts' },
      activityInfo: {
        status: 'error',
      },
      error: {
        message: error?.message || String(error),
        stack: error?.stack || undefined,
      },
    });
    return null;
  } finally {
    // Set loading state to false
    dispatch(authActions.setGenerateFactorLoading({ isLoading: false }));
  }
});

/**
 * Used to update UI with the new auth state after a successful log in
 */
export const refreshAuth = thunkActionFactory(async ({ dispatch, state, businessLogic }) => {
  const {
    config: { ssrtid, portal },
    auth: { authToken },
    sale: { type },
    insight,
  } = state;
  const token = insightSelectors.tokenSelector(insight);

  const authPayload = await refreshAuthBL({
    portal,
    ssrtid,
    authToken,
    token,
  });
  dispatch(authActions.refreshAuthenticationSuccessful(authPayload));
  if (authPayload.auth.isUserSignedIn && type !== 'SUBSCRIPTION') {
    dispatch(setIsSavePaymentMethodChecked(true));
  }
  businessLogic.auth.updateData(authPayload.auth);
});

// Create a debounced version of generate factor
const debouncedGenerateFactor = makeDebounce();

/**
 * Debounced generate factor action that re-validates conditions at execution time
 */
export const debouncedGenerateFactorAction = thunkActionFactory(async ({ dispatch, state }) => {
  const {
    auth: { frictionlessLoginSessionId, frictionlessCheckout },
    auth: { isUserSignedIn },
    featureFlags,
    ixp,
  } = state;
  const currentEmail = frictionlessCheckout?.email;

  // Check if this is subscription or forced autopay flow to determine which field to check
  const isForcedAutopayRecurring = isForcedAutopayRecurringSelector(state);
  const isSubscriptionFLPhase2 = isFrictionlessPhase2SubscriptionsEnabled({ featureFlags, ixp });

  const currentIsEmailExists =
    isSubscriptionFLPhase2 || isForcedAutopayRecurring
      ? !frictionlessCheckout.isUserNameAvailable
      : false;

  // Triple-check that the email still doesn't exist and we still need to generate factor
  // This is the critical validation to prevent generate factor for new emails
  if (
    !currentEmail ||
    currentEmail.trim() === '' ||
    !currentIsEmailExists || // This should be true only for existing emails
    frictionlessLoginSessionId ||
    isUserSignedIn
  ) {
    return;
  }

  // Additional safety check - only proceed if email definitely exists
  if (!currentIsEmailExists) {
    return;
  }

  try {
    await dispatch(generateFactor());
    // Trigger frictionless login check to show the modal
    dispatch(checkFrictionlessLogin());
  } catch (error: any) {
    // Log error but don't throw to avoid breaking the flow
    splunkReporter.contextual({
      event: 'pay',
      action: 'frictionlessLogin',
      logInfo: { logLevel: 'error', logger: 'store/auth/slice.ts' },
      activityInfo: {
        status: 'error',
      },
      error: {
        message: error?.message || String(error),
        stack: error?.stack || undefined,
      },
    });
  }
});

/**
 * Handle real-time email changes for frictionless login
 * This action is called when email input changes and should trigger frictionless login modal
 * if the email exists (user is not new)
 */
export const handleEmailChangeForFrictionlessLogin = thunkActionFactory(
  async ({ dispatch, state }) => {
    const {
      auth: { frictionlessLoginSessionId, frictionlessLoginEmail },
      featureFlags,
      ixp,
    } = state;
    const frictionlessCheckoutEmail = state.auth.frictionlessCheckout?.email;
    const isSubscriptionFLPhase2 = isFrictionlessPhase2SubscriptionsEnabled({ featureFlags, ixp });
    const isForcedAutopayRecurring = isForcedAutopayRecurringSelector(state);

    // Only proceed if we have a valid email
    if (!frictionlessCheckoutEmail || frictionlessCheckoutEmail.trim() === '') {
      // Clear frictionless login state for flows that support frictionless
      if (
        (isSubscriptionFLPhase2 || isForcedAutopayRecurring) &&
        (frictionlessLoginEmail || frictionlessLoginSessionId)
      ) {
        dispatch(authActions.clearFrictionlessLoginState());
      }
      return;
    }

    // Validate email format before proceeding
    if (!emailRegex.test(frictionlessCheckoutEmail)) {
      // Clear frictionless login state for invalid emails
      if (
        (isSubscriptionFLPhase2 || isForcedAutopayRecurring) &&
        (frictionlessLoginEmail || frictionlessLoginSessionId)
      ) {
        dispatch(authActions.clearFrictionlessLoginState());
      }
      return;
    }

    // Check if frictionless subscriptions phase 2 OR forced autopay is enabled
    if (!isSubscriptionFLPhase2 && !isForcedAutopayRecurring) {
      return;
    }

    // Check if user is not signed in
    const isUserSignedIn = state.auth.isUserSignedIn;
    if (isUserSignedIn) {
      return;
    }

    // Check if email exists (user is not new) based on the flow type
    const isEmailExists =
      isSubscriptionFLPhase2 || isForcedAutopayRecurring
        ? !state.auth.frictionlessCheckout.isUserNameAvailable
        : false;

    // If email doesn't exist (new user), clear frictionless login state to prevent modal from showing
    if (!isEmailExists) {
      if (frictionlessLoginEmail || frictionlessLoginSessionId) {
        dispatch(authActions.clearFrictionlessLoginState());
      }
      return;
    }

    // If we already have a session ID for this email, don't regenerate
    if (frictionlessLoginSessionId) {
      return;
    }

    try {
      // Set the email for frictionless login
      dispatch(
        authActions.setFrictionlessLoginEmail({
          email: frictionlessCheckoutEmail,
        })
      );

      // Debounced generate factor token call with longer delay to ensure availability check completes
      debouncedGenerateFactor(() => {
        dispatch(debouncedGenerateFactorAction());
      }, 1000); // Increased delay to ensure availability check completes
    } catch (error: any) {
      // Log error but don't throw to avoid breaking the flow
      splunkReporter.contextual({
        event: 'pay',
        action: 'frictionlessLogin',
        logInfo: { logLevel: 'error', logger: 'store/auth/slice.ts' },
        activityInfo: {
          status: 'error',
        },
        error: {
          message: error?.message || String(error),
          stack: error?.stack || undefined,
        },
      });
    }
  }
);
